import { create } from 'zustand'
import { clearCheckoutDraft } from '@/lib/checkoutDraft'
import { authService, LoginResponse } from '@/services/authService'
import { serializeUserForStorage } from '@/lib/authStorage'
import {
  legacyUserRoleService,
  type LegacyUserRoles,
} from '@/services/legacyUserRoleService'

interface User {
  id: string
  email: string
  firstName?: string
  lastName?: string
  name?: string
  sid?: string
  legacyUserpk?: number
  emailVerified?: boolean
  isActive?: boolean
  isDeleted?: boolean
  profilePictureUrl?: string
  picture?: string
  isEmployee?: boolean
  isFacilitator?: boolean
  isAdministrator?: boolean
  isInstructor?: boolean
  communityAdminNotification?: boolean
  legacy_campus_register?: boolean
  legacy_district_register?: boolean
  legacy_global_register?: boolean
  orgId?: number
  siteId?: number
  locationId?: number
  locationOrganizationId?: number
  locationSiteId?: number
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isLoading: boolean
  error: string | null
  requiresEmailVerification: boolean
  legacyRoles: LegacyUserRoles | null
  legacyRolesSid: string | null
  isLegacyRolesLoading: boolean
  
  // Actions
  setAuthenticated: (isAuthenticated: boolean) => void
  setUser: (user: User | null) => void
  setAccessToken: (token: string | null) => void
  setRefreshToken: (token: string | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setRequiresEmailVerification: (requires: boolean) => void
  login: (email: string, password: string) => Promise<LoginResponse>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  clearError: () => void
  refreshAccessToken: () => Promise<boolean>
  applyProfileLocation: (location: { orgId: number; siteId: number; locationId: number }) => Promise<void>
  fetchLegacyRoles: () => Promise<void>
}

// Initialize state from localStorage synchronously
const initializeAuthState = () => {
  const accessToken = localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken')
  const userStr = localStorage.getItem('user')

  let user = null
  if (userStr) {
    try {
      user = JSON.parse(userStr)
    } catch (e) {
      console.error('Error parsing stored user data:', e)
    }
  }

  const isAuthenticated = !!(accessToken && user)

  return {
    isAuthenticated,
    user,
    accessToken,
    refreshToken,
    isLoading: false, // We have synchronously loaded from localStorage, so not loading
    error: null,
    requiresEmailVerification: false,
    legacyRoles: null,
    legacyRolesSid: null,
    isLegacyRolesLoading: false,
  }
}

const initialState = initializeAuthState()

let legacyRolesRequest: { sid: string; promise: Promise<void> } | null = null

const toBooleanFlag = (value: unknown): boolean => {
  const normalizedValue = Array.isArray(value) ? value[0] : value
  if (typeof normalizedValue === 'boolean') return normalizedValue
  if (typeof normalizedValue === 'number') return normalizedValue === 1
  if (typeof normalizedValue === 'string') {
    return ['true', '1', 'yes', 'y'].includes(normalizedValue.trim().toLowerCase())
  }
  return false
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...initialState,

  setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setUser: (user) => {
    const currentState = get()
    const isAuthenticated = !!(user && currentState.accessToken)
    const userChanged = currentState.user?.sid !== user?.sid
    set({
      user,
      isAuthenticated,
      ...(userChanged
        ? { legacyRoles: null, legacyRolesSid: null, isLegacyRolesLoading: false }
        : {}),
    })
    if (user) {
      localStorage.setItem('user', serializeUserForStorage(user))
    } else {
      localStorage.removeItem('user')
    }
  },
  setAccessToken: (accessToken) => {
    const currentState = get()
    const isAuthenticated = !!(accessToken && currentState.user)
    set({ accessToken, isAuthenticated })
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken)
    } else {
      localStorage.removeItem('accessToken')
    }
  },
  setRefreshToken: (refreshToken) => {
    set({ refreshToken })
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken)
    } else {
      localStorage.removeItem('refreshToken')
    }
  },
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setRequiresEmailVerification: (requiresEmailVerification) => set({ requiresEmailVerification }),
  
  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await authService.login(email, password)
      
      if (response.success && response.accessToken) {
        // Get user from localStorage (includes decoded JWT data with name from legacy_fname/legacy_lname)
        const storedUser = authService.getUser()

        // Login successful, now fetch the actual profile to check email verification
        try {
          const profileResponse = await authService.getProfile()

          if (profileResponse.success && profileResponse.profile) {
            const profile = profileResponse.profile
            
            // Check if email is verified (profile has isEmailVerified field)
            if (profile.isEmailVerified === false) {
              // authService.login() always stores tokens/user on a successful
              // credential check, even though the account isn't verified yet.
              // Clear them here so checkAuth() can't later treat this as a
              // valid, verified session from stale localStorage.
              localStorage.removeItem('accessToken')
              localStorage.removeItem('refreshToken')
              localStorage.removeItem('user')

              set({
                isAuthenticated: false,
                user: null,
                accessToken: null,
                refreshToken: null,
                requiresEmailVerification: true,
                isLoading: false
              })
              return { ...response, user: storedUser, requiresEmailVerification: true }
            }

            // Email is verified - set authenticated state
            // ALWAYS use stored user data (from JWT) which has the name from legacy_fname/legacy_lname
            // Also fetch the full user profile to get isEmployee flag
            let isEmployee = false
            let profilePictureUrl: string | undefined
            let isFacilitator = false
            let isAdministrator = false
            let isInstructor = false
            let legacyCampusRegister = false
            let legacyDistrictRegister = false
            let legacyGlobalRegister = false
            let orgId = 0
            let siteId = 0
            let locationId = 0
            let locationOrganizationId = 0
            let locationSiteId = 0
            const tokenRegisterRoles = authService.getLegacyRegisterRoles()
            const tokenCommunityAdminNotification = authService.getCommunityAdminNotificationRole()
            try {
              const { userProfileService } = await import('@/services/userProfileService')
              const fullProfile = await userProfileService.getUserProfile()
              isEmployee      = fullProfile.isEmployee    || false
              profilePictureUrl = fullProfile.profilePictureUrl || undefined
              isFacilitator   = fullProfile.facilitator   || false
              isAdministrator = fullProfile.administrator || false
              isInstructor    = fullProfile.instructor    || false
              legacyCampusRegister = toBooleanFlag(fullProfile.legacy_campus_register) || tokenRegisterRoles.legacy_campus_register
              legacyDistrictRegister = toBooleanFlag(fullProfile.legacy_district_register) || tokenRegisterRoles.legacy_district_register
              legacyGlobalRegister = toBooleanFlag(fullProfile.legacy_global_register) || tokenRegisterRoles.legacy_global_register
              orgId = fullProfile.locationOrganizationId || fullProfile.orgId || 0
              siteId = fullProfile.locationSiteId || fullProfile.siteId || 0
              locationId = fullProfile.locationId || 0
              locationOrganizationId = fullProfile.locationOrganizationId || orgId
              locationSiteId = fullProfile.locationSiteId || siteId
            } catch (profileErr) {
              console.error('Error fetching employee status:', profileErr)
              legacyCampusRegister = tokenRegisterRoles.legacy_campus_register
              legacyDistrictRegister = tokenRegisterRoles.legacy_district_register
              legacyGlobalRegister = tokenRegisterRoles.legacy_global_register
            }
            
            const userWithName = {
              ...storedUser,
              id: storedUser?.email || profile.email,
              email: storedUser?.email || profile.email,
              emailVerified: profile.isEmailVerified,
              isActive: true,
              isDeleted: false,
              isEmployee,
              profilePictureUrl: profilePictureUrl || storedUser?.profilePictureUrl,
              isFacilitator,
              isAdministrator,
              isInstructor,
              communityAdminNotification: tokenCommunityAdminNotification,
              legacy_campus_register: legacyCampusRegister,
              legacy_district_register: legacyDistrictRegister,
              legacy_global_register: legacyGlobalRegister,
              orgId,
              siteId,
              locationId,
              locationOrganizationId,
              locationSiteId,
            }
            
            set({
              isAuthenticated: true,
              user: userWithName,
              accessToken: response.accessToken || null,
              refreshToken: response.refreshToken || null,
              requiresEmailVerification: false,
              isLoading: false
            })
            
            // Pre-fetch recommendations — display is gated by the LD enable-recommendations flag
            import('@/stores/recommendationsStore').then(({ useRecommendationsStore }) => {
              useRecommendationsStore.getState().fetchRecommendations(userWithName.email, 10)
            }).catch(() => {})

            return { ...response, user: userWithName, requiresEmailVerification: false }
          } else {
            // Profile fetch failed - but we have JWT token, so use that
            set({
              isAuthenticated: true,
              user: storedUser,
              accessToken: response.accessToken || null,
              refreshToken: response.refreshToken || null,
              requiresEmailVerification: false,
              isLoading: false
            })
            
            // Pre-fetch recommendations — display is gated by the LD enable-recommendations flag
            import('@/stores/recommendationsStore').then(({ useRecommendationsStore }) => {
              const email = storedUser?.email
              if (email) {
                useRecommendationsStore.getState().fetchRecommendations(email, 10)
              }
            }).catch(() => {})

            return { ...response, user: storedUser, requiresEmailVerification: false }
          }
        } catch (profileError) {
          // Profile endpoint failed - use JWT data anyway since we have a valid token
          set({
            isAuthenticated: true,
            user: storedUser,
            accessToken: response.accessToken || null,
            refreshToken: response.refreshToken || null,
            requiresEmailVerification: false,
            isLoading: false
          })

          // Pre-fetch recommendations — display is gated by the LD enable-recommendations flag
          import('@/stores/recommendationsStore').then(({ useRecommendationsStore }) => {
            const email = storedUser?.email
            if (email) {
              useRecommendationsStore.getState().fetchRecommendations(email, 10)
            }
          }).catch(() => {})
          
          return { ...response, user: storedUser, requiresEmailVerification: false }
        }
      } else {
        set({ 
          error: response.message || 'Login failed',
          isLoading: false 
        })
      }
      
      return response
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Login failed'
      set({ 
        error: errorMessage,
        isLoading: false 
      })
      throw error
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true, error: null })
      await authService.logout()
      set({ 
        isAuthenticated: false, 
        user: null, 
        accessToken: null,
        refreshToken: null,
        requiresEmailVerification: false,
        legacyRoles: null,
        legacyRolesSid: null,
        isLegacyRolesLoading: false,
        isLoading: false 
      })
      
      // Clear recommendations on logout
      import('@/stores/recommendationsStore').then(({ useRecommendationsStore }) => {
        useRecommendationsStore.getState().clearRecommendations()
      }).catch(() => {})
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Logout failed',
        isLoading: false 
      })
    }
  },

  checkAuth: async () => {
    try {
      const accessToken = authService.getAccessToken()
      const user = authService.getUser()

      if (accessToken && user) {
        // User has valid stored session
        const userName = user.firstName && user.lastName 
          ? `${user.firstName} ${user.lastName}`.trim()
          : user.firstName || user.lastName || user.name || user.email;
        
        // Fetch full user profile to get isEmployee flag
        let isEmployee = user.isEmployee || false
        let profilePictureUrl: string | undefined = user.profilePictureUrl
        let isFacilitator   = user.isFacilitator   || false
        let isAdministrator = user.isAdministrator || false
        let isInstructor    = user.isInstructor    || false
        let legacyCampusRegister = user.legacy_campus_register || false
        let legacyDistrictRegister = user.legacy_district_register || false
        let legacyGlobalRegister = user.legacy_global_register || false
        let orgId = user.locationOrganizationId || user.orgId || 0
        let siteId = user.locationSiteId || user.siteId || 0
        let locationId = user.locationId || 0
        let locationOrganizationId = user.locationOrganizationId || orgId
        let locationSiteId = user.locationSiteId || siteId
        const tokenRegisterRoles = authService.getLegacyRegisterRoles()
        const tokenCommunityAdminNotification = authService.getCommunityAdminNotificationRole()
        try {
          const { userProfileService } = await import('@/services/userProfileService')
          const fullProfile = await userProfileService.getUserProfile()
          isEmployee      = fullProfile.isEmployee    || false
          profilePictureUrl = fullProfile.profilePictureUrl || profilePictureUrl
          isFacilitator   = fullProfile.facilitator   || false
          isAdministrator = fullProfile.administrator || false
          isInstructor    = fullProfile.instructor    || false
          legacyCampusRegister = toBooleanFlag(fullProfile.legacy_campus_register) || tokenRegisterRoles.legacy_campus_register
          legacyDistrictRegister = toBooleanFlag(fullProfile.legacy_district_register) || tokenRegisterRoles.legacy_district_register
          legacyGlobalRegister = toBooleanFlag(fullProfile.legacy_global_register) || tokenRegisterRoles.legacy_global_register
          orgId = fullProfile.locationOrganizationId || fullProfile.orgId || 0
          siteId = fullProfile.locationSiteId || fullProfile.siteId || 0
          locationId = fullProfile.locationId || 0
          locationOrganizationId = fullProfile.locationOrganizationId || orgId
          locationSiteId = fullProfile.locationSiteId || siteId
        } catch (profileErr) {
          console.error('Error fetching employee status:', profileErr)
          legacyCampusRegister = tokenRegisterRoles.legacy_campus_register
          legacyDistrictRegister = tokenRegisterRoles.legacy_district_register
          legacyGlobalRegister = tokenRegisterRoles.legacy_global_register
        }
        
        set({
          isAuthenticated: true,
          user: {
            ...user,
            name: userName,
            isEmployee,
            profilePictureUrl,
            isFacilitator,
            isAdministrator,
            isInstructor,
            communityAdminNotification: tokenCommunityAdminNotification,
            legacy_campus_register: legacyCampusRegister,
            legacy_district_register: legacyDistrictRegister,
            legacy_global_register: legacyGlobalRegister,
            orgId,
            siteId,
            locationId,
            locationOrganizationId,
            locationSiteId,
          },
          accessToken,
          refreshToken: authService.getRefreshToken(),
          isLoading: false
          // DON'T clear error here - let it persist for user to see
        })
        
        // Pre-fetch recommendations — display is gated by the LD enable-recommendations flag
        import('@/stores/recommendationsStore').then(({ useRecommendationsStore }) => {
          const email = user?.email
          if (email) {
            useRecommendationsStore.getState().fetchRecommendations(email, 10)
          }
        }).catch(() => {})
      } else {
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          legacyRoles: null,
          legacyRolesSid: null,
          isLegacyRolesLoading: false,
          isLoading: false
          // DON'T clear error here - let it persist for user to see
        })
      }
    } catch (error) {
      set({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        legacyRoles: null,
        legacyRolesSid: null,
        isLegacyRolesLoading: false,
        error: error instanceof Error ? error.message : 'Authentication check failed',
        isLoading: false
      })
    }
  },

  refreshAccessToken: async () => {
    try {
      const refreshToken = get().refreshToken || authService.getRefreshToken()
      
      if (!refreshToken) {
        return false
      }

      const response = await authService.refreshToken(refreshToken)
      
      if (response.success && response.accessToken) {
        set({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken || refreshToken,
        })
        return true
      }
      
      return false
    } catch (error) {
      // If refresh fails, logout
      await get().logout()
      return false
    }
  },

  applyProfileLocation: async ({ orgId, siteId, locationId }) => {
    const withLocation = (user: User | null) => user
      ? { ...user, orgId, locationOrganizationId: orgId, siteId, locationSiteId: siteId, locationId }
      : user

    const previous = get().user
    const unchanged = previous &&
      (previous.locationOrganizationId || previous.orgId) === orgId &&
      (previous.locationSiteId || previous.siteId) === siteId &&
      previous.locationId === locationId &&
      authService.getLegacySiteId() === siteId
    set(state => ({ user: withLocation(state.user) }))
    if (unchanged) return

    // The 401 interceptor rotates the refresh token in localStorage only, so it is the current one.
    const refreshToken = authService.getRefreshToken() || get().refreshToken
    if (!refreshToken) return

    // Reissue the token so its legacy location claims match the saved profile. A failure here is
    // not fatal and must not sign the participant out: the claims catch up on the next refresh.
    try {
      const response = await authService.refreshToken(refreshToken, { skipAuthRedirect: true })
      if (response.success && response.accessToken) {
        set({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken || refreshToken,
        })
      }
    } catch {
      // Ignore; see above.
    } finally {
      const user = get().user
      if (user) localStorage.setItem('user', serializeUserForStorage(user))
    }
  },

  fetchLegacyRoles: async () => {
    const sid = get().user?.sid?.trim()

    if (!sid) {
      set({
        legacyRoles: null,
        legacyRolesSid: null,
        isLegacyRolesLoading: false,
      })
      return
    }

    if (get().legacyRolesSid === sid) return
    if (legacyRolesRequest?.sid === sid) return legacyRolesRequest.promise

    set({ isLegacyRolesLoading: true })

    const promise = (async () => {
      try {
        const legacyRoles = await legacyUserRoleService.getUserRoles(sid)
        if (get().user?.sid === sid) {
          set({ legacyRoles, legacyRolesSid: sid })
        }
      } catch (error) {
        console.error('Error fetching legacy user roles:', error)
        if (get().user?.sid === sid) {
          set({ legacyRoles: null, legacyRolesSid: sid })
        }
      } finally {
        if (get().user?.sid === sid) {
          set({ isLegacyRolesLoading: false })
        }
        if (legacyRolesRequest?.sid === sid) {
          legacyRolesRequest = null
        }
      }
    })()

    legacyRolesRequest = { sid, promise }
    return promise
  },

  clearError: () => {
    set({ error: null })
  },
}))

useAuthStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id || state.isAuthenticated !== previous.isAuthenticated) {
    clearCheckoutDraft()
  }
})
